import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import SubPageHero from '@/components/SubPageHero/SubPageHero';
import StackedContent from '@/components/StackedContent/StackedContent';
import heroPageIcon from '@/assets/images/bullseye.svg';
import QuickLink from '@/components/QuickLink/QuickLink';
import Accordion from '@/components/Accordion/Accordion';
import { useOktaAuth } from "@okta/okta-react";
import { useSchool } from '@context/SchoolContext';
import { useOverviewContext } from '@context/OverviewContext';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import { useLinksContext } from "@context/LinksContext";
import { useTasklistDisplayRuntime } from '@/hooks/useTasklistDisplayRuntime/useTasklistDisplayRuntime';
import { sendFlexDetails } from '@/utils/trackHeapUser';
import config from "../../config";
import { WpErrorSection } from '@/components/ErrorFallbackSection/ErrorFallbackSection';

const API_STUDENT_PROGRAM_INFO = config.api.endpoints.STUDENT_PROGRAM_INFO;

const MakeAccordionItem = (eventKey: string, title: string, content: string | string[]) => {
  if (!Array.isArray(content)) content = [content]

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>{title}</Accordion.Header>
      <Accordion.Body>
        {content.map((contentItem: string, index: number) => {
          return (<div key={title + index} dangerouslySetInnerHTML={{ __html: contentItem }} />)
        })}
      </Accordion.Body>
    </Accordion.Item>
  );
};

const AboutSchool = () => {
  const { selectedStudent } = useSelectedStudent();
  const { authState } = useOktaAuth();
  const oktaAccessToken = authState?.accessToken?.accessToken;
  const [hasEnrolledFlex, setHasEnrolledFlex] = useState(false);

  const studentName =
  selectedStudent?.nickName || selectedStudent?.firstName || "";

  useEffect(() => {
    if (!oktaAccessToken) {
      console.warn("No Okta token available. No API call.");
      return;
    }

    const fetchData = async () => {
      try {
        const response = await fetch(API_STUDENT_PROGRAM_INFO(String(selectedStudent?.accountId)), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${oktaAccessToken}`,
            "Content-Type": "application/json",
          },
        });

        const result = await response.json();

        setHasEnrolledFlex(result.hasFlexProgram);
      } catch (error) {
        throw new Error('Failed to fetch student program info');
      }
    };

    fetchData();
  }, [oktaAccessToken, selectedStudent?.accountId]);

  const { userRoleVal, identityId: lcIdentityId } = useOverviewContext();
  const isLC = userRoleVal === 'LEARNING_COACH';
  const isPrivateSchool = config.app.privateSchoolIds.includes(selectedStudent?.schoolId || '');
  const { data: schoolData } = useSchool();
  const {
    school_name: wpSchoolName = '',
    abbreviation: wpAbbreviation = '',
  } = schoolData || {};
  // WP API failure Banner Updates
  const heroSchoolAbbreviation = wpAbbreviation || selectedStudent?.schoolShortName || '';
  const heroSchoolName = wpSchoolName || selectedStudent?.schoolName || '';

  const {
    first_week_description: firstWeekDescription,
    student_orientation_live: studentOrientationLive,
    student_orientation_recorded: studentOrientationRecorded,
    learning_coach_orientation: learningCoachOrientation,
    meet_the_teacher_event: meetTheTeacherEvent,
    standardized_testing_requirements: standardizedTestingRequirements,
    class_schedule_instructions: classScheduleInstructions,
    general_school_expectations: generalSchoolExpectations,
    attendance_content_standard: attendanceContentStandard,
    attendance_content_flex: attendanceContentFlex,
    homework,
    quizzes_and_tests: quizzesAndTests,
    quizzes_and_tests_learning_coach: quizzesAndTestsLearningCoach,
    flex_program_enrolled: flexProgramEnrolled,
    flex_program_not_enrolled: flexProgramNotEnrolled,
    standardized_testing: standardizedTesting,
    iep,
    login_information: loginInformation,
  } = schoolData?.onboarding_content || {};

  // Check if WP API failed onboarding content
  const noOnboardingContent = !schoolData?.onboarding_content;

  // Heap - Send Flex Status
  useEffect(() => {
    if (
      selectedStudent?.identityId &&
      selectedStudent?.schoolId &&
      typeof hasEnrolledFlex === 'boolean'
    ) {
      sendFlexDetails({
        id: isLC && lcIdentityId ? lcIdentityId : selectedStudent.identityId,
        studentIdentityId: isLC ? selectedStudent.identityId : undefined,
        studentFlex: hasEnrolledFlex,
        isLC,
      });
    }
  }, [selectedStudent, hasEnrolledFlex, isLC, lcIdentityId]);

  const breadcrumbItems = [
    { title: "Home", path: "/" },
    { title: "School Information", path: "/schoolinfo" },
    { title: `What to Know About Attending ${heroSchoolAbbreviation}` }
  ];

  const QuickLinks = () => {
    const { schoolLink } = useLinksContext();
    const { showCanvasInlineLinks, isReady: k12PortalReady } = useTasklistDisplayRuntime();
    const hasCalendar = !!schoolData?.calendar_link;
    const hasOlsSchedule =
      k12PortalReady && showCanvasInlineLinks && Boolean(schoolLink.schoolLinkUrl);

    if (!hasCalendar && !hasOlsSchedule) return;

    return (
      <StackedContent alignStart>
        <StackedContent.Col1 cols='4'>
          <h2>Quick Links</h2>
        </StackedContent.Col1>
        <StackedContent.Col2 cols='6'>
          {hasCalendar && (
            <QuickLink
              title={`${heroSchoolAbbreviation} Academic Calendar`}
              linkUrl={`${schoolData?.calendar_link}`}
              linkTarget='_blank'
              buttonText="See the calendar"
            />
          )}

          {hasOlsSchedule && (
            <QuickLink
              title={`${studentName}'s Schedule`}
              linkUrl={`${schoolLink.schoolLinkUrl}calendar`}
              linkTarget="_blank"
              buttonText="Go to the schedule in OLS"
            />
          )}
        </StackedContent.Col2>
      </StackedContent>
    );
  };

  const FirstWeek = () => {
    const FirstWeekDescription = () => {
      if (!firstWeekDescription) return;
      return (<div dangerouslySetInnerHTML={{ __html: firstWeekDescription }} />)
    }

    const StudentOrientation = () => {
      if (!studentOrientationLive && !studentOrientationRecorded) return;
      return MakeAccordionItem('0', 'Student Orientation', [
        studentOrientationLive,
        studentOrientationRecorded
      ]);
    }

    const LearningCoachOrientation = () => {
      if (!learningCoachOrientation || !isLC) return;
      return MakeAccordionItem('1', 'Learning Coach Orientation', learningCoachOrientation);
    }

    const MeetTheTeacher = () => {
      if (!meetTheTeacherEvent) return;
      return MakeAccordionItem('2', 'Meet the Teacher', meetTheTeacherEvent);
    }

    const StandardizedTestingRequirements = () => {
      if (!standardizedTestingRequirements) return;
      return MakeAccordionItem('3', 'Beginning-of-Year Assessments', standardizedTestingRequirements);
    }

    return (
      <StackedContent alignStart>
        <StackedContent.Col1 cols="4">
          <h2>{selectedStudent?.firstName}'s First Week</h2>
        </StackedContent.Col1>
        <StackedContent.Col2 cols="6">
          <FirstWeekDescription />
          <Accordion>
            <StudentOrientation />
            <LearningCoachOrientation />
            <MeetTheTeacher />
            <StandardizedTestingRequirements />
          </Accordion>
        </StackedContent.Col2>
      </StackedContent>
    );
  };

  const ClassSchedule = () => {
    if (!classScheduleInstructions) return;

    return (
      <StackedContent alignStart>
        <StackedContent.Col1 cols="4">
          <h2>How to Find {studentName}'s Class Schedule</h2>
        </StackedContent.Col1>
        <StackedContent.Col2 cols="6">
          <div dangerouslySetInnerHTML={{ __html: classScheduleInstructions }} />
        </StackedContent.Col2>
      </StackedContent>
    );
  };

  const GeneralSchoolExpectations = () => {
    const GeneralSchoolExpectationsDescription = () => {
      if (!generalSchoolExpectations) return;
      return (<div dangerouslySetInnerHTML={{ __html: generalSchoolExpectations }} />)
    }

    const Attendance = () => {
      if ((!attendanceContentStandard && !attendanceContentFlex) || isPrivateSchool) return;
      const content = hasEnrolledFlex ? attendanceContentFlex : attendanceContentStandard;
      return MakeAccordionItem('4', 'Attendance', content);
    }

    const Homework = () => {
      if (!homework) return;
      return MakeAccordionItem('5', 'Homework', homework);
    }

    const QuizzesAndTests = () => {
      if (!quizzesAndTests && !quizzesAndTestsLearningCoach) return;
      const content = isLC ? quizzesAndTestsLearningCoach : quizzesAndTests;
      const heading = (isLC ? 'Learning Coach ' : '') + 'Quizzes & Tests';
      return MakeAccordionItem('6', heading, content || '');
    }

    return (
      <StackedContent alignStart>
        <StackedContent.Col1 cols="4">
          <h2>General School Expectations</h2>
        </StackedContent.Col1>
        <StackedContent.Col2 cols="6">
          <GeneralSchoolExpectationsDescription />
          <Accordion>
            <Attendance />
            <Homework />
            <QuizzesAndTests />
          </Accordion>
        </StackedContent.Col2>
      </StackedContent>
    );
  };

  const AboutProgram = () => {
    if ((!flexProgramEnrolled && !flexProgramNotEnrolled) || isPrivateSchool) return;
    const programType = hasEnrolledFlex ? 'Flex' : 'Standard';
    const content = hasEnrolledFlex ? flexProgramEnrolled : flexProgramNotEnrolled;

    return (
      <StackedContent alignStart>
        <StackedContent.Col1 cols="4">
          <h2>About the {programType} Program</h2>
        </StackedContent.Col1>
        <StackedContent.Col2 cols="6">
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </StackedContent.Col2>
      </StackedContent>
    )
  };

  const StandardizedTesting = () => {
    if (!standardizedTesting || !isLC) return;

    return (
      <StackedContent alignStart>
        <StackedContent.Col1 cols="4">
          <h2>In-Person Requirements for Standardized State Testing</h2>
        </StackedContent.Col1>
        <StackedContent.Col2 cols="6">
          <div dangerouslySetInnerHTML={{ __html: standardizedTesting }} />
        </StackedContent.Col2>
      </StackedContent>
    );
  }

  return (
    <div className="about-attending-school WP-Fallback">
      <div className="lc-role-ols-container">
        <div className="inner-container">
          <Breadcrumb items={breadcrumbItems} />
          <div className="subpage-hero-container">
            <SubPageHero
              theme="secondary"
              title={`What to Know About Attending ${heroSchoolAbbreviation}`}
              description={`Here's everything you need to know to get off to a great start at ${heroSchoolName}, from what to expect during the first week, to what your school will expect of you.`}
              image={heroPageIcon}
            />
          </div>
        </div>
      </div>

      {noOnboardingContent ? (
        <div className="error-page">
          <WpErrorSection />
        </div>
      ) : (
        <>
          <QuickLinks />
          <FirstWeek />
          <ClassSchedule />
          <GeneralSchoolExpectations />
          <AboutProgram />
          <StandardizedTesting />
          <StackedContent justifyStart>
            <StackedContent.Col1 cols='10'>
              <h2>FAQs</h2>
              <Accordion>
                {isLC && !isPrivateSchool && MakeAccordionItem('8', 'My child needs special education services (IEP/504). When will I get to discuss their needs?', iep || '')}
                {MakeAccordionItem('9', 'What do I do if I forget my login information?', loginInformation || '')}
              </Accordion>
            </StackedContent.Col1>
          </StackedContent>
        </>
      )}

    </div>
  );
};

export default AboutSchool;
